import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0108 from "../lesson-08/lesson";

const linkLesson0108 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0108.id);

const theory = ` <h3 class=sectionHead>Wave equation for spherical waves</h3>
<p>
   A wave is said spherical when all the points at the same distance from a given point (source point
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>O</mi>
   </math>
   ) vibrate in phase. The pressure is therefore a function of time and distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   to the point source. The wave equation writes
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂r</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>∂p</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂r</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>or else
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi mathvariant=italic>rp</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>r</mi>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi mathvariant=italic>rp</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<h3 class=sectionHead>Solution for pressure</h3>
<p>
   It is easily verified that any function of the form
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>rp</mi>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>±</mo>
      <mi mathvariant=italic>ct</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is a solution. The solution of the wave equation for spherical waves centered at 0 is written as
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mi>f</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>ct</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>ct</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>g</mi>
   </math>
   are any function. The first term corresponds to a diverging wave (moving away from the point 0) while the second term corresponds to a converging wave (which approaches the point 0). Note that when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   tends to zero, the pressure tends to infinity. In the case of a divergent wave, this is not really a problem as a source is never punctual in practice. In the case of a converging wave, the pressure may actually reach a very high value which can be extremely destructive (in the case of head approximately spherical wave that propagates in the brain can cause serious damage; in the case of cavitation, collapsing bubbles of gas can cause serious damage and even be a source of light [sonoluminescence]). In harmonic regime of pulsation
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ω</mi>
   </math>, the general solution can be written
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>Note that, for a divergent wave, the pressure amplitude decreases 6dB per doubling of distance due to the expansion of the wave. This decrease is sometimes called geometric attenuation (different from an absorption since the acoustic energy is conserved).
<h3 class=sectionHead>Solution for particle velocity</h3>
<p>
   To calculate the particle velocity it is necessary to use the Euler equation:
   <strong>
      <mstyle class=text>
         <mtext>grad</mtext>
      </mstyle>
   </strong>
   <mi>p</mi>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-bin>−</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mfrac>
      <mrow>
         <mi>∂</mi>
         <strong>
            <mstyle class=text>
               <mtext>v</mtext>
            </mstyle>
         </strong>
      </mrow>
      <mrow>
         <mi mathvariant=italic>∂t</mi>
      </mrow>
   </mfrac>. Considering that due to the symmetry of the problem the velocity vector is radial (
   <strong>
      <mstyle class=text>
         <mtext>v</mtext>
      </mstyle>
   </strong>
   <mo class=MathClass-rel>=</mo>
   <mi>v</mi>
   <mfrac>
      <mrow>
         <strong>
            <mstyle class=text>
               <mtext>r</mtext>
            </mstyle>
         </strong>
      </mrow>
      <mrow>
         <mi>r</mi>
      </mrow>
   </mfrac>) this reduces for a spherical wave in the frequency domain to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>∂p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂r</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi mathvariant=italic>ωv</mi>
   </math>. So the particle velocity is given by
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>v</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>A</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>cr</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>B</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>cr</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>As convergent waves are rarely observed, we will consider in the following only divergent waves given by
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>v</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>A</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>cr</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   In this equation the first term
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi mathvariant=italic>cr</mi>
         </mrow>
      </mfrac>
   </math>
   corresponds to the far field as it is dominant when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mn>1</mn>
   </math>
   Note that, as for the plane wave, the far field term is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>. The second term
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>near</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi mathvariant=italic>cjk</mi>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>
   corresponds to the near field as it is dominant when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>. This term is in quadrature with the pressure which means that it do not carry energy: this is called the reactive field.
<p>The impedance of the divergent spherical wave is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<h3 class=sectionHead>pulsating sphere</h3>
<p>
   Consider a pulsating sphere of radius
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
   </math>, that is to say, a sphere on which every point vibrates in phase with the same amplitude
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
   </math>. Assuming that the sphere oscillates in infinite space, the boundary condition on the surface of the sphere being supposed perfectly reflecting allows to write
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>R</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>v</mi>
   </math>
   is the air particle velocity. Given the results of the previous Lesson, we can write
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>A</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkR</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>cR</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkR</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   Setting
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>4</mn>
      <mi>π</mi>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
   </math>
   the acoustic volume velocity we deduce
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>A</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkR</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πR</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkR</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   Setting
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jkR</mi>
         </mrow>
      </msup>
   </math>
   we deduce the pressure radiated by the sphere
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πrR</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkR</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>The impedance at the surface of the sphere is called the radiation impedance and is given by
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>jkR</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>kR</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>kR</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>kR</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   The real part reflects the ability of the source to radiate (indeed mean intensity is
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>I</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi mathvariant=bold>Re</mi>
   <mo class=MathClass-open>(</mo>
   <msup>
      <mrow>
         <mi>p</mi>
      </mrow>
      <mrow>
         <mo class=MathClass-bin>∗</mo>
      </mrow>
   </msup>
   <mi>v</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mn>1</mn>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </mfrac>
   <mi mathvariant=bold>Re</mi>
   <mo class=MathClass-open>(</mo>
   <mi>Z</mi>
   <mo class=MathClass-close>)</mo>
   <msup>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msup>
   </math>, (see <a href="${linkLesson0108}" target="_blank">Lesson 1.8</a>). For low frequencies (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>kR</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>) radiation is very low, it actually becomes significant for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>kR</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
   </math>
   becomes maximal (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>4</mn>
            <mi>π</mi>
            <msup>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>) for high frequencies (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>kR</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mn>1</mn>
   </math>). This result is illustrated in <a href="#" data-img-src=${config.ASSETS_URL}/01/13/theory/figure-1.svg data-img-alt="Figure 1. Normalized radiation impedance of pulsating sphere. <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub></math> is the characteristic impedance given by <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub><mo class=MathClass-rel>=</mo><mfrac><mrow><msub><mrow><mi>ρ</mi></mrow><mrow><mn>0</mn></mrow></msub><mi>c</mi></mrow><mrow><mn>4</mn><mi>π</mi><msup><mrow><mi>R</mi></mrow><mrow><mn>2</mn></mrow></msup></mrow></mfrac></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>R</mi></math> the radius of the sphere">Figure 1</a>. A small pulsating sphere is doubly handicapped compared to a larger source by the fact that its flow is necessarily lower and that its radiation ability is lower.
   <figure class=figure>
<p><img alt="Figure 1. Normalized radiation impedance of pulsating sphere. <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub></math> is the characteristic impedance given by <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub><mo class=MathClass-rel>=</mo><mfrac><mrow><msub><mrow><mi>ρ</mi></mrow><mrow><mn>0</mn></mrow></msub><mi>c</mi></mrow><mrow><mn>4</mn><mi>π</mi><msup><mrow><mi>R</mi></mrow><mrow><mn>2</mn></mrow></msup></mrow></mfrac></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>R</mi></math> the radius of the sphere" src=${config.ASSETS_URL}/01/13/theory/figure-1.svg><figcaption class=caption>Figure 1. Normalized radiation impedance of pulsating sphere. <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub></math> is the characteristic impedance given by <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub><mo class=MathClass-rel>=</mo><mfrac><mrow><msub><mrow><mi>ρ</mi></mrow><mrow><mn>0</mn></mrow></msub><mi>c</mi></mrow><mrow><mn>4</mn><mi>π</mi><msup><mrow><mi>R</mi></mrow><mrow><mn>2</mn></mrow></msup></mrow></mfrac></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>R</mi></math> the radius of the sphere</figcaption></figure>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
<dt>[1]
<dd> L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
<dt>[2]
<dd> M. Bruneau. Manuel d’Acoustique Fondamentale. Hermes, Paris, 1998.
<dt>[3]
<dd> A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
<dt>[4]
<dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
<dt>[5]
<dd> E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
<dt>[6]
<dd> S. Temkin. Elements of Acoustics. Wiley, New York, 1981.`;

export default theory;
